export const INDICATOR_HISTORY_WINDOW_MS = 2_000;
export const INDICATOR_HISTORY_MAX_POINTS = 48;

export type IndicatorName = "DA" | "OP" | "NA" | "Ach" | "VP" | "5-HT";
export type IndicatorSide = "driver" | "vehicle";
export type OperationSystem = "steering" | "throttle" | "brake" | "gear";

export interface IndicatorHistoryPoint {
  raceTimeMs: number;
  value: number;
  causalId?: string;
}

export interface IndicatorContribution {
  side: IndicatorSide;
  indicator: IndicatorName;
  raceTimeMs: number;
  causalId: string;
  scores: Record<OperationSystem, number>;
  hasDominantOperation: boolean;
  leadingOperation: OperationSystem | null;
}

interface TimedRecord {
  race_time_ms: number;
}

function lowerBound<T extends TimedRecord>(records: readonly T[], targetMs: number): number {
  let low = 0;
  let high = records.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (records[middle].race_time_ms < targetMs) low = middle + 1;
    else high = middle;
  }
  return low;
}

function upperBound<T extends TimedRecord>(records: readonly T[], targetMs: number): number {
  let low = 0;
  let high = records.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (records[middle].race_time_ms <= targetMs) low = middle + 1;
    else high = middle;
  }
  return low;
}

export function selectRecentIndicatorHistoryFromRecords<T extends TimedRecord>(
  records: readonly T[],
  raceTimeMs: number,
  valueOf: (record: T) => number,
  causalIdOf?: (record: T) => string | undefined,
  windowMs = INDICATOR_HISTORY_WINDOW_MS,
  maxPoints = INDICATOR_HISTORY_MAX_POINTS,
): IndicatorHistoryPoint[] {
  if (!records.length || !Number.isFinite(raceTimeMs) || windowMs <= 0 || maxPoints <= 0) return [];
  const startIndex = lowerBound(records, raceTimeMs - windowMs);
  const endIndex = upperBound(records, raceTimeMs);
  const count = endIndex - startIndex;
  if (count <= 0) return [];
  const outputCount = Math.min(count, maxPoints);
  const result: IndicatorHistoryPoint[] = [];
  for (let outputIndex = 0; outputIndex < outputCount; outputIndex += 1) {
    const offset = outputCount === 1 ? count - 1 : Math.round(outputIndex * (count - 1) / (outputCount - 1));
    const record = records[startIndex + offset];
    const value = valueOf(record);
    if (!Number.isFinite(value)) continue;
    const causalId = causalIdOf?.(record);
    result.push({
      raceTimeMs: record.race_time_ms,
      value,
      ...(causalId === undefined ? {} : { causalId }),
    });
  }
  return result;
}

export function selectRecentIndicatorHistory(
  points: readonly IndicatorHistoryPoint[],
  raceTimeMs: number,
  windowMs = INDICATOR_HISTORY_WINDOW_MS,
  maxPoints = INDICATOR_HISTORY_MAX_POINTS,
): IndicatorHistoryPoint[] {
  if (!Number.isFinite(raceTimeMs) || windowMs <= 0 || maxPoints <= 0) return [];
  const start = raceTimeMs - windowMs;
  const eligible = points.filter(point => Number.isFinite(point.raceTimeMs)
    && Number.isFinite(point.value)
    && point.raceTimeMs >= start
    && point.raceTimeMs <= raceTimeMs);
  if (eligible.length <= maxPoints) return eligible;
  if (maxPoints === 1) return [eligible.at(-1)!];
  const selected: IndicatorHistoryPoint[] = [];
  for (let index = 0; index < maxPoints; index += 1) {
    const sourceIndex = Math.round(index * (eligible.length - 1) / (maxPoints - 1));
    const point = eligible[sourceIndex];
    if (selected.at(-1) !== point) selected.push(point);
  }
  return selected;
}

export function fixedScaleHistoryPath(
  points: readonly IndicatorHistoryPoint[],
  width = 112,
  height = 26,
): string {
  if (points.length === 0 || width <= 0 || height <= 0) return "";
  const firstTime = points[0].raceTimeMs;
  const lastTime = points.at(-1)!.raceTimeMs;
  const timeSpan = Math.max(1, lastTime - firstTime);
  return points.map((point, index) => {
    const x = points.length === 1 ? width : (point.raceTimeMs - firstTime) / timeSpan * width;
    const y = (1 - Math.max(0, Math.min(1, point.value))) * height;
    return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
}

export function selectCurrentContribution(
  contributions: readonly IndicatorContribution[],
  side: IndicatorSide,
  indicator: IndicatorName,
  raceTimeMs: number,
  maxAgeMs = 250,
): IndicatorContribution | null {
  const eligible = contributions.filter(item => item.side === side
    && item.indicator === indicator
    && item.raceTimeMs <= raceTimeMs
    && item.raceTimeMs >= raceTimeMs - maxAgeMs);
  return eligible.reduce<IndicatorContribution | null>(
    (latest, item) => !latest || item.raceTimeMs > latest.raceTimeMs ? item : latest,
    null,
  );
}

export function operationLabel(operation: OperationSystem): string {
  return ({ steering: "操舵", throttle: "スロットル", brake: "ブレーキ", gear: "ギア" })[operation];
}
